import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { deleteEntry, restoreEntry } from "./api";

const UNDO_MS = 8000;

// Self-managed dismissal so pause-on-interaction is deterministic (not reliant on sonner
// internals): the timer is cleared on hover/focus/touch and restarted on leave/blur/touch-end.
function UndoToast({ entryId, toastId, onRestored }) {
  const timer = useRef(null);
  const [restoring, setRestoring] = useState(false);

  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const start = () => { clear(); timer.current = setTimeout(() => toast.dismiss(toastId), UNDO_MS); };

  useEffect(() => { start(); return clear; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const undo = () => {
    clear();
    setRestoring(true);
    restoreEntry(entryId)
      .then((e) => {
        onRestored && onRestored(e);
        toast.dismiss(toastId);
        toast.success("Restored.");
      })
      .catch(() => {
        setRestoring(false);
        start(); // give them another chance to retry
        toast.error("Couldn’t undo — the item wasn’t restored. Please try again.");
      });
  };

  return (
    <div
      data-testid="undo-toast"
      onMouseEnter={clear}
      onMouseLeave={start}
      onFocus={clear}
      onBlur={start}
      onTouchStart={clear}
      onTouchEnd={start}
      className="flex items-center gap-3 rounded-2xl bg-[var(--ink)] text-[var(--on-pine)] pl-5 pr-2 py-2.5 shadow-[0_12px_40px_rgba(20,32,27,0.35)] min-w-[290px] max-w-[92vw]"
    >
      <span className="text-[15px] font-medium flex-1 leading-snug">Item deleted.</span>
      <button
        type="button"
        data-testid="undo-delete-btn"
        onClick={undo}
        disabled={restoring}
        className="shrink-0 rounded-full bg-[var(--surface)] hover:bg-[var(--surface)] active:bg-[var(--surface)] disabled:opacity-60 text-[var(--on-pine)] font-semibold px-5 text-sm min-h-[44px] min-w-[92px] transition-colors duration-150"
      >
        Undo
      </button>
    </div>
  );
}

// One destructive verb everywhere: Delete. Recoverable via a CATCHABLE Undo toast
// (and the "Recently deleted" backstop in Settings). Backend soft-deletes, so Undo just
// restores the entry to its previous status — title/cue/type/area/owner/visibility intact.
export function deleteWithUndo(id, { onDeleted, onRestored } = {}) {
  return deleteEntry(id)
    .then(() => {
      onDeleted && onDeleted();
      toast.custom((t) => <UndoToast entryId={id} toastId={t} onRestored={onRestored} />, {
        duration: Infinity, // dismissal is controlled by UndoToast's own pause-aware timer
      });
    })
    .catch(() => toast.error("Couldn’t delete that."));
}
